import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

function ArticleProcessor() {
  const [article, setArticle] = useState('');
  const [processedWords, setProcessedWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not logged in
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!article.trim()) {
      setError('Please enter an article to process');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      const response = await fetch('/api/process-article', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ article })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process article');
      }
      
      const data = await response.json();
      setProcessedWords(data.words);
    } catch (err) {
      setError('Error processing article: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToKnown = async (word) => {
    try {
      await fetch('/api/vocabulary/known', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ word })
      });
      
      // Remove word from processed list
      setProcessedWords(processedWords.filter(w => w.word !== word.word));
    } catch (err) {
      setError('Error adding word to known list: ' + err.message);
      console.error(err);
    }
  };

  const handleAddToLearning = async (word) => {
    try {
      await fetch('/api/vocabulary/learning', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ word })
      });
      
      // Remove word from processed list
      setProcessedWords(processedWords.filter(w => w.word !== word.word));
    } catch (err) {
      setError('Error adding word to learning list: ' + err.message);
      console.error(err);
    }
  };

  return (
    <div className="article-processor">
      <div className="card">
        <h1>Process an Article</h1>
        {error && <div style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="article">Paste your English article below:</label>
            <textarea
              id="article"
              rows="10"
              value={article}
              onChange={(e) => setArticle(e.target.value)}
              placeholder="Paste your English article here..."
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="btn" 
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Process Article'}
          </button>
        </form>
      </div>

      {processedWords.length > 0 && (
        <div className="card">
          <h2>Words to Review ({processedWords.length})</h2>
          <p>These words are not in your known words list. Review and categorize them:</p>
          
          <div className="word-list">
            {processedWords.map((wordItem) => (
              <div key={wordItem.word} className="word-card">
                <div className="word-info">
                  <h3>{wordItem.word}</h3>
                  {wordItem.translation && <p>{wordItem.translation}</p>}
                </div>
                <div className="word-actions">
                  <button 
                    className="btn btn-small" 
                    onClick={() => handleAddToKnown(wordItem)}
                  >
                    Add to Known
                  </button>
                  <button 
                    className="btn btn-small btn-secondary" 
                    onClick={() => handleAddToLearning(wordItem)}
                  >
                    Add to Learning
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ArticleProcessor;
